import React, { useEffect, useState } from "react";
import api, { apiError } from "@/api";
import { PageTitle, GlassCard, EmptyState } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Megaphone, Send, Trash2, Users, School as SchoolIcon, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Instructions() {
  const [schools, setSchools] = useState([]);
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("");
  const [sending, setSending] = useState(false);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/instructions").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/schools").then((r) => setSchools(r.data || [])).catch(() => {}); load(); }, []);

  const send = async () => {
    if (!title.trim() || !message.trim()) return toast.error("العنوان والنص مطلوبان");
    setSending(true);
    try {
      await api.post("/instructions", { title, message, school_id: target || null });
      toast.success("تم إرسال التوجيه إلى جرس المنسقين");
      setTitle(""); setMessage(""); setTarget(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  const remove = async () => {
    try { await api.delete(`/instructions/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const fmt = (d) => { try { return new Date(d).toLocaleString("ar"); } catch { return ""; } };

  return (
    <div>
      <InspireBanner />

      <PageTitle title="توجيهات المنسقين" subtitle="أرسل تعليماتك لتصل فوراً إلى جرس إشعارات المنسقين" icon={Megaphone} />

      <GlassCard className="mb-6" data-testid="instruction-compose">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-stone-500 mb-1 block">عنوان التوجيه *</label>
            <input data-testid="instruction-title" value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="مثال: تذكير برفع الشواهد قبل نهاية الأسبوع" />
          </div>
          <div>
            <label className="text-xs text-stone-500 mb-1 block">نص التوجيه *</label>
            <textarea data-testid="instruction-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className={inputCls} placeholder="اكتب التعليمات بوضوح..." />
          </div>
          <div>
            <label className="text-xs text-stone-500 mb-1 block">المستهدَف</label>
            <select data-testid="instruction-target" value={target} onChange={(e) => setTarget(e.target.value)} className={inputCls}>
              <option value="">كل المنسقين (توجيه عام)</option>
              {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <button data-testid="send-instruction-btn" onClick={send} disabled={sending} className="btn-gold px-6 py-2 rounded-xl flex items-center gap-2 disabled:opacity-50">
            <Send size={18} /> {sending ? "جارٍ الإرسال..." : "إرسال التوجيه"}
          </button>
        </div>
      </GlassCard>

      {!items.length ? (
        <EmptyState text="لم تُرسل توجيهات بعد" icon={Megaphone} />
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <GlassCard key={it.id} data-testid={`instruction-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-head font-bold text-stone-800">{it.title}</h3>
                  <p className="text-sm text-stone-600 whitespace-pre-wrap mt-1">{it.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-stone-500 flex-wrap">
                    <span className="inline-flex items-center gap-1">{it.school_id ? <SchoolIcon size={12} /> : <Users size={12} />} {it.school_name}</span>
                    <span className="inline-flex items-center gap-1"><CheckCheck size={12} /> اطّلع: {it.read_count}</span>
                    <span>{fmt(it.created_at)}</span>
                  </div>
                </div>
                <button data-testid={`del-instruction-${it.id}`} onClick={() => setDelId(it.id)} className="p-2 rounded-lg text-red-600 hover:bg-red-50 shrink-0"><Trash2 size={16} /></button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف التوجيه</AlertDialogTitle>
            <AlertDialogDescription>سيُحذف من سجل التوجيهات (لن يظهر للمنسقين بعدها).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-del-instruction" onClick={remove} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
